import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, ArrowUpRight, ShieldCheck } from "lucide-react";
import { SECTORS } from "@/data/sectors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function SectorPage() {
  const { slug } = useParams();
  const sector = SECTORS.find((s) => s.id === slug);
  const [insight, setInsight] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setInsight(null);
    if (sector) {
      axios
        .get(`${API}/insights`)
        .then((r) => setInsight(r.data.find((p) => p.slug === sector.insight) || null))
        .catch(() => {});
    }
  }, [slug, sector]);

  useEffect(() => {
    if (!sector) return;
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    document.title = `${sector.name} | Sectors | BYOT Innovations`;
    meta?.setAttribute("content", `${sector.name}: ${sector.desc}`);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, [sector]);

  if (!sector) {
    return (
      <main data-testid="sector-not-found" className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-5 text-center">
        <h1 className="font-serif text-4xl text-[#F7F5F0]">Sector not found.</h1>
        <a href="/#sectors" className="mt-6 inline-flex items-center gap-2 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">
          <ArrowLeft size={14} /> All sectors
        </a>
      </main>
    );
  }

  const index = SECTORS.indexOf(sector);
  void index;

  return (
    <main data-testid="sector-page" className="min-h-screen">
      <section className="relative flex min-h-[52vh] items-end overflow-hidden">
        <motion.img
          src={sector.img}
          alt={sector.alt}
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2, ease: EASE }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[#070E1B]/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070E1B] via-[#070E1B]/40 to-[#070E1B]/70" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-14 pt-40 sm:px-8">
          <motion.a
            href="/#sectors"
            data-testid="sector-back-link"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="inline-flex items-center gap-2 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#CBD5E1] transition-colors hover:text-[#D4AF37]"
          >
            <ArrowLeft size={14} /> Sectors We Serve
          </motion.a>
          <div className="overflow-hidden">
            <motion.h1
              data-testid="sector-title"
              initial={{ y: "115%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: EASE }}
              className="mt-3 font-serif text-4xl tracking-tight text-[#F7F5F0] sm:text-5xl lg:text-6xl"
            >
              {sector.name}
            </motion.h1>
          </div>
          <motion.p
            data-testid="sector-tagline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            className="mt-4 max-w-xl text-base text-[#CBD5E1] sm:text-lg"
          >
            {sector.tagline}
          </motion.p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-14 px-5 py-20 sm:px-8 md:py-24 lg:grid-cols-[1.2fr_1fr]">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <p className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Focus Areas</p>
          <div className="mt-7 flex flex-col">
            {sector.focus.map((f, i) => (
              <div
                key={f}
                data-testid={`sector-focus-${i}`}
                className="flex items-start gap-4 border-b border-[rgba(212,175,55,0.12)] py-5"
              >
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-[#D4AF37]" />
                <p className="text-base leading-relaxed text-[#E2E8F0]">{f}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.12, ease: EASE }}
        >
          <p className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Credentials in Play</p>
          <div className="mt-7 flex flex-col gap-4">
            {sector.credentials.map((c, i) => (
              <div
                key={c.title}
                data-testid={`sector-credential-${i}`}
                className="border border-[rgba(212,175,55,0.18)] bg-[#0F1A2E]/70 p-6"
              >
                <div className="flex items-center gap-3">
                  <ShieldCheck size={18} className="shrink-0 text-[#D4AF37]" />
                  <h3 className="font-serif text-lg text-[#F7F5F0]">{c.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#94A3B8]">{c.note}</p>
              </div>
            ))}
          </div>

          {insight && (
            <div className="mt-10">
              <p className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Relevant Insight</p>
              <a
                href={`/insights/${insight.slug}`}
                data-testid="sector-insight-link"
                className="group mt-5 block border border-[rgba(212,175,55,0.18)] bg-[#0F1A2E]/70 p-6 transition-colors duration-500 hover:border-[#D4AF37]/55"
              >
                <div className="flex items-center justify-between font-mono-alt text-[10px] uppercase tracking-[0.22em] text-[#64748B]">
                  <span>{formatDate(insight.date)}</span>
                  <span>{insight.read_time}</span>
                </div>
                <h3 className="mt-4 font-serif text-xl leading-snug text-[#F7F5F0] transition-colors duration-300 group-hover:text-[#E5C158]">
                  {insight.title}
                </h3>
                <span className="mt-5 flex items-center gap-2 font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">
                  Read <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </a>
            </div>
          )}
        </motion.div>
      </section>

      <section className="border-t border-[rgba(212,175,55,0.15)] bg-[#0A1222]/60">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-7 px-5 py-16 sm:px-8 md:flex-row md:items-center md:justify-between">
          <h2 className="max-w-xl font-serif text-2xl text-[#F7F5F0] sm:text-3xl">
            Building in <em className="gold-text not-italic">{sector.name.toLowerCase()}</em>? The strategy desk responds within one business day.
          </h2>
          <a
            href="/#contact"
            data-testid="sector-cta-contact"
            className="group flex shrink-0 items-center gap-3 bg-[#D4AF37] px-7 py-4 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#070E1B] transition-colors duration-300 hover:bg-[#F3E5AB]"
          >
            Request Consultation
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      </section>
    </main>
  );
}
